import { useState } from 'react';
import {
  Building2,
  Calendar,
  Users,
  User,
  Home,
  Megaphone,
  ShoppingCart,
  HandHeart,
  Search,
  Car,
  ChevronRight,
  BookOpen,
  Utensils,
  MapPin,
  Wrench,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { useOrgNavMode } from '../../client/contexts/OrgNavModeContext';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { HUB_LANDING_PATH } from '../../shared/hubLocations';

const COMMUNITY_PATHS = [
  '/buy-sell',
  '/request-offer',
  '/lost-found',
  '/transportation',
  '/professionals',
];

const DIRECTORY_PATHS = ['/tenants', '/food-court', '/facilities', '/services'];

const COMMUNITY_SECTIONS = [
  { label: 'Buy & Sell',       path: '/buy-sell',       icon: ShoppingCart,  color: 'text-green-600' },
  { label: 'Request & Offer',  path: '/request-offer',  icon: HandHeart,     color: 'text-indigo-600' },
  { label: 'Lost & Found',     path: '/lost-found',     icon: Search,        color: 'text-blue-500' },
  { label: 'Transportation',   path: '/transportation', icon: Car,           color: 'text-sky-600' },
];

const DIRECTORY_SECTIONS = [
  { label: 'Tenants',    path: '/tenants',    icon: Building2, color: 'text-cyan-600' },
  { label: 'Food Court', path: '/food-court', icon: Utensils,  color: 'text-orange-600' },
  { label: 'Facilities', path: '/facilities', icon: MapPin,    color: 'text-teal-600' },
  { label: 'Services',   path: '/services',   icon: Wrench,    color: 'text-purple-500' },
];

const EVENT_SECTIONS = [
  { label: 'Events / Activities', path: '/events',  icon: Calendar,  color: 'text-blue-500' },
  { label: 'Notices',             path: '/notices', icon: Megaphone, color: 'text-purple-600' },
];

function getActiveTab(
  pathname: string
): 'home' | 'directory' | 'events' | 'community' | 'account' | null {
  if (pathname.startsWith('/account')) return 'account';
  if (pathname.includes('/events') || pathname.includes('/notices')) return 'events';
  if (COMMUNITY_PATHS.some((p) => pathname.includes(p))) return 'community';
  if (DIRECTORY_PATHS.some((p) => pathname.includes(p))) return 'directory';
  if (pathname.includes('/information-desk')) return 'home';
  return null;
}

export default function MobileTabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setNavMode } = useOrgNavMode();
  const { selectedLocation } = useLocationContext();
  const [communityOpen, setCommunityOpen] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);

  const activeTab = getActiveTab(pathname);

  function locationSlug(): string | null {
    return selectedLocation?.slug ?? selectedLocation?.locationCode?.toLowerCase() ?? null;
  }

  /** Location-scoped path: /{slug}/section — matches main.wasp routes and CommonSidebar. */
  function locPath(basePath: string): string {
    const slug = locationSlug();
    return slug ? `/${slug}${basePath}` : HUB_LANDING_PATH;
  }

  function handleCommunitySelect(path: string) {
    setNavMode('community');
    setCommunityOpen(false);
    navigate(locPath(path));
  }

  function handleDirectorySelect(path: string) {
    setDirectoryOpen(false);
    navigate(locPath(path));
  }

  function handleEventsSelect(path: string) {
    setEventsOpen(false);
    navigate(locPath(path));
  }

  const tabs = [
    {
      id: 'home' as const,
      label: 'Home',
      icon: Home,
      onClick: () => navigate(locPath('/information-desk')),
    },
    {
      id: 'directory' as const,
      label: 'Directory',
      icon: BookOpen,
      onClick: () => setDirectoryOpen(true),
    },
    {
      id: 'events' as const,
      label: 'Events',
      icon: Calendar,
      onClick: () => setEventsOpen(true),
    },
    {
      id: 'community' as const,
      label: 'Community',
      icon: Users,
      onClick: () => setCommunityOpen(true),
    },
    {
      id: 'account' as const,
      label: 'Account',
      icon: User,
      onClick: () => navigate('/account'),
    },
  ];

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-20 lg:hidden bg-header-bg border-t border-header-border"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex">
          {tabs.map(({ id, label, icon: Icon, onClick }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={onClick}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[3.5rem] py-1 transition-colors',
                  isActive ? 'text-primary' : 'text-header-text-muted hover:text-content-text'
                )}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[10px] font-medium leading-none">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <Sheet open={eventsOpen} onOpenChange={setEventsOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl lg:hidden">
          <SheetHeader className="pb-2">
            <SheetTitle>Events &amp; Notices</SheetTitle>
          </SheetHeader>
          <div className="pb-8">
            {EVENT_SECTIONS.map(({ label, path, icon: Icon, color }) => (
              <button
                key={path}
                onClick={() => handleEventsSelect(path)}
                className={cn(
                  'w-full flex items-center gap-3 px-1 py-3.5 border-b border-border last:border-0 transition-colors',
                  pathname.includes(path) ? 'text-primary' : 'text-content-text hover:bg-sidebar-hover'
                )}
              >
                <Icon className={cn('w-5 h-5 flex-shrink-0', color)} />
                <span className="flex-1 text-left text-sm font-medium">{label}</span>
                <ChevronRight className="w-4 h-4 text-content-text-muted flex-shrink-0" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={directoryOpen} onOpenChange={setDirectoryOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl lg:hidden max-h-[75vh]">
          <SheetHeader className="pb-2">
            <SheetTitle>Directory</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto pb-8">
            {DIRECTORY_SECTIONS.map(({ label, path, icon: Icon, color }) => (
              <button
                key={path}
                onClick={() => handleDirectorySelect(path)}
                className={cn(
                  'w-full flex items-center gap-3 px-1 py-3.5 border-b border-border last:border-0 transition-colors',
                  pathname.includes(path) ? 'text-primary' : 'text-content-text hover:bg-sidebar-hover'
                )}
              >
                <Icon className={cn('w-5 h-5 flex-shrink-0', color)} />
                <span className="flex-1 text-left text-sm font-medium">{label}</span>
                <ChevronRight className="w-4 h-4 text-content-text-muted flex-shrink-0" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={communityOpen} onOpenChange={setCommunityOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl lg:hidden max-h-[75vh]">
          <SheetHeader className="pb-2">
            <SheetTitle>Community</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto pb-8">
            {COMMUNITY_SECTIONS.map(({ label, path, icon: Icon, color }) => (
              <button
                key={path}
                onClick={() => handleCommunitySelect(path)}
                className={cn(
                  'w-full flex items-center gap-3 px-1 py-3.5 border-b border-border last:border-0 transition-colors',
                  pathname.includes(path) ? 'text-primary' : 'text-content-text hover:bg-sidebar-hover'
                )}
              >
                <Icon className={cn('w-5 h-5 flex-shrink-0', color)} />
                <span className="flex-1 text-left text-sm font-medium">{label}</span>
                <ChevronRight className="w-4 h-4 text-content-text-muted flex-shrink-0" />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
